import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useRef, useState } from 'react';
import AttachmentOrderForm, {
  AttachmentOrderFormRef,
  AttachmentOrderFormValues,
} from './AttachmentOrderForm';
import { IAttachmentOrder } from '@nxsys/types/models/attachmentOrder';
import {
  defaultAttachmentOderDesOptions,
  defaultSubcontractorStatusOption,
} from '@nxsys/constants/AppConstants';

type EditAttachmentOrderProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: AttachmentOrderFormValues) => Promise<void>;
  data: IAttachmentOrder;
};

const EditAttachmentOrderModal: React.FC<EditAttachmentOrderProps> = ({
  isOpen,
  toggle,
  onSubmit,
  data,
}: EditAttachmentOrderProps) => {
  const { messages } = useTypedIntl();

  const formRef = useRef<AttachmentOrderFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: AttachmentOrderFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['common.editAttachmentOrder']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      <AttachmentOrderForm
        formRef={formRef}
        onSubmit={handleSubmit}
        isEdit
        defaultValues={{
          type:
            defaultAttachmentOderDesOptions.find(
              (item) => item.value === data.deductionDesc,
            )?.type || 'Table',
          deductionDesc:
            defaultAttachmentOderDesOptions.find(
              (item) => item.value === data.deductionDesc,
            ) || null,
          status: data.isActive
            ? defaultSubcontractorStatusOption[0]
            : defaultSubcontractorStatusOption[1],
          priority: data.priority?.toString() ?? '',
          effectiveDate: data.effectiveDate,
          endDate: data.endDate,
          deductionRate: data.deductionRate?.toString() ?? '',
          protectedMin: data.protectedMin?.toString() ?? '',
          protectedMinType: (data.protectedMinType as 'percentage' | 'amount') ?? 'percentage',
          marginFee: !!data.marginFee,
          orderRef: data.orderRef,
          totalAttachment: data.totalAttachment?.toString() ?? '',
          deductionThisPeriod: Number(data.deductionThisPeriod || 0).toFixed(2),
          totalDeduction: Number(data.totalDeduction || 0).toFixed(2),
        }}
      />
    </AppModalContainer>
  );
};

export default EditAttachmentOrderModal;
